/**
 * When an announcement may interrupt. Every rule errs towards not showing:
 * a skipped load costs nothing (nothing is recorded, a later load tries
 * again), an interruption at the wrong moment costs goodwill.
 */

/** Screens that run unattended or drive equipment: never cover them. */
const QUIET_PATHS: RegExp[] = [
  /^\/login/,
  /^\/brewcontrol/,
  /^\/devices/,
  /^\/fermentations\/dashboards/,
  /^\/fermentations\/procedures\/[^/]+\/dashboard/,
  /^\/harvests\/[^/]+\/live/,
];

export function isQuietPath(pathname: string): boolean {
  return QUIET_PATHS.some((pattern) => pattern.test(pathname));
}

export interface MomentSnapshot {
  pathname: string;
  /** Automation (Playwright and friends) must never meet a popup. */
  webdriver: boolean;
  hidden: boolean;
  viewportWidth: number;
  /** Someone is typing: a field has focus. */
  typing: boolean;
  /** Something else is already asking for attention (alerts, editors). */
  dialogOpen: boolean;
}

/** Below this the rail and the animation don't make sense. */
export const MIN_VIEWPORT_WIDTH = 788;

export function isGoodMoment(m: MomentSnapshot): boolean {
  return (
    !isQuietPath(m.pathname) &&
    !m.webdriver &&
    !m.hidden &&
    m.viewportWidth >= MIN_VIEWPORT_WIDTH &&
    !m.typing &&
    !m.dialogOpen
  );
}

export function readMoment(): MomentSnapshot {
  const active = document.activeElement;
  return {
    pathname: window.location.pathname,
    webdriver: navigator.webdriver === true,
    hidden: document.visibilityState !== "visible",
    viewportWidth: window.innerWidth,
    typing:
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      active instanceof HTMLSelectElement ||
      (active instanceof HTMLElement && active.isContentEditable),
    dialogOpen:
      document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      ) !== null,
  };
}
